import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

// Load initial state from localStorage or use default
const loadInitialState = () => {
  try {
    const savedCart = localStorage.getItem('wholesaleCart');
    if (savedCart) {
      return JSON.parse(savedCart);
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
  }
  return {
    items: [],
    total: 0,
  };
};

const initialState = loadInitialState();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      // Check if item already exists (using _id for MongoDB)
      const existingItem = state.items.find(item => item._id === action.payload._id);
      if (existingItem) {
        // If price changed, update it; otherwise keep existing
        const newPrice = action.payload.pricing?.unitPrice || existingItem.pricing?.unitPrice;
        const priceChanged = newPrice !== existingItem.pricing?.unitPrice;
        
        return {
          ...state,
          items: state.items.map(item =>
            item._id === action.payload._id 
              ? { 
                  ...item, 
                  quantity: item.quantity + action.payload.quantity,
                  pricing: {
                    ...item.pricing,
                    unitPrice: newPrice  // Update to latest price
                  },
                  priceChanged: priceChanged  // Flag for UI to show
                } 
              : item
          ),
        };
      }
      return { ...state, items: [...state.items, action.payload] };
    
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter((item) => item._id !== action.payload),
      };
    
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item._id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };
    
    case 'CLEAR_CART':
      return {
        items: [],
        total: 0,
      };
    
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('wholesaleCart', JSON.stringify(state));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [state]);

  const addToCart = (product, quantity = 1) => {
    dispatch({ type: 'ADD_TO_CART', payload: { ...product, quantity } });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
    }
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const value = {
    items: state.items,
    total: state.total,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};